import { useEffect, useState } from "react";
import { AlertTriangle, Pencil, Plus, Trash2 } from "lucide-react";
import { fmtDT, isOverdue, nowLocalDT, useStore } from "../store";
import { useCrud } from "../useCrud";
import {
  Btn, Card, ConfirmDelete, Empty, Field, IconBtn, Modal, PageHead, Pill, Select, Table, Td, inputCls, statusTone,
} from "../ui";

const PRIORITY = ["High", "Medium", "Normal"];
const STATUS = ["Open", "Solved"];
const blank = () => ({ datetime: nowLocalDT(), name: "", problem: "", priority: "Normal", solution: "", status: "Open" });
const validate = (f) => {
  const e = {};
  if (!f.datetime) e.datetime = "Date & time is required";
  if (!f.name?.trim()) e.name = "Name is required";
  if (!f.problem?.trim()) e.problem = "Problem is required";
  return e;
};
const prioTone = { High: "red", Medium: "yellow", Normal: "zinc" };

export const useTick = () => {
  const [, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);
};

const ProblemForm = ({ c }) => (
  <form onSubmit={c.save} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="problem-form">
    <Field label="Date & Time *" error={c.errors.datetime}><input type="datetime-local" className={inputCls} value={c.form.datetime} onChange={c.change("datetime")} data-testid="problem-datetime-input" /></Field>
    <Field label="Name *" error={c.errors.name}><input className={inputCls} value={c.form.name} onChange={c.change("name")} placeholder="Whose problem?" data-testid="problem-name-input" /></Field>
    <Field label="Problem *" error={c.errors.problem} className="sm:col-span-2"><textarea rows={2} className={inputCls} value={c.form.problem} onChange={c.change("problem")} data-testid="problem-text-input" /></Field>
    <Field label="Priority"><Select options={PRIORITY} value={c.form.priority} onChange={c.change("priority")} data-testid="problem-priority-select" /></Field>
    <Field label="Status"><Select options={STATUS} value={c.form.status} onChange={c.change("status")} data-testid="problem-status-select" /></Field>
    <Field label="Solution" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={c.form.solution} onChange={c.change("solution")} data-testid="problem-solution-input" /></Field>
    <div className="flex justify-end gap-2 sm:col-span-2">
      <Btn type="button" variant="ghost" onClick={c.close} data-testid="problem-cancel-btn">Cancel</Btn>
      <Btn type="submit" data-testid="problem-save-btn">Save problem</Btn>
    </div>
  </form>
);

const ProblemRow = ({ p, hours, c }) => {
  const overdue = isOverdue(p, hours);
  return (
    <tr className={`transition-colors ${overdue ? "bg-rose-500/[0.06] hover:bg-rose-500/10" : "hover:bg-white/[0.02]"}`} data-testid={`problem-row-${p.id}`}>
      <Td className="whitespace-nowrap">{fmtDT(p.datetime)}</Td>
      <Td className="whitespace-nowrap font-medium text-zinc-100">{p.name}</Td>
      <Td className="max-w-[260px]">{p.problem}</Td>
      <Td><Pill tone={prioTone[p.priority]}>{p.priority}</Pill></Td>
      <Td className="max-w-[240px] text-zinc-400">{p.solution || "—"}</Td>
      <Td className="whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Pill tone={statusTone(p.status)}>{p.status}</Pill>
          {overdue && <span className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-bold tracking-wider text-white" data-testid={`problem-overdue-${p.id}`}><AlertTriangle className="h-3 w-3" />OVERDUE</span>}
        </div>
      </Td>
      <Td className="whitespace-nowrap">
        <IconBtn onClick={() => c.openEdit(p)} data-testid={`problem-edit-${p.id}`}><Pencil className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => c.setDelId(p.id)} className="hover:text-rose-400" data-testid={`problem-delete-${p.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
      </Td>
    </tr>
  );
};

export default function Problems() {
  useTick();
  const { problems, settings } = useStore();
  const c = useCrud("problems", blank, validate, "Problem");
  const rows = [...problems].sort((a, b) => b.datetime.localeCompare(a.datetime));
  return (
    <div>
      <PageHead title="Problems" sub={`Unsolved problems turn OVERDUE after ${settings.problemHours} hours.`}>
        <Btn onClick={() => c.openNew()} data-testid="add-problem-btn"><Plus className="h-4 w-4" />Add Problem</Btn>
      </PageHead>
      <Card className="animate-rise [animation-delay:80ms]">
        {rows.length ? (
          <Table head={["Date & Time", "Name", "Problem", "Priority", "Solution", "Status", ""]}>
            {rows.map((p) => <ProblemRow key={p.id} p={p} hours={settings.problemHours} c={c} />)}
          </Table>
        ) : <Empty testid="problems-empty" title="No problems logged" hint="Log a problem as soon as someone raises it." />}
      </Card>
      <Modal open={!!c.form} onClose={c.close} title={c.editId ? "Edit Problem" : "Add Problem"} testid="problem-modal">
        {c.form && <ProblemForm c={c} />}
      </Modal>
      <ConfirmDelete open={!!c.delId} onCancel={() => c.setDelId(null)} onConfirm={c.confirmDelete} what="this problem" />
    </div>
  );
}
